// Import firebase services
import { firebaseAuth, companiesModel } from '@/firebase/firebase-config'

// Import the mixins
import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'
import { GenerateUID } from '@/mixins/helpers/GenerateUID'

export const CompanyRepository = {

	/*
	**  Register the mixins
	*/

	mixins: [

		BugsnagService,
		GenerateUID

	],

	/*
	**  Set up the data
	*/

	data: () => ({

		new_company: {
			id: null,
			type: null,
			meta: {
				created_at: moment().unix(),
				updated_at: moment().unix(),
				deleted_at: null,
				app_id: '1:KA82LQU7lsJy:eKwkfzaQy4aM:HKUl9ACoGeAQ:j1xHCCBLS7UQ:yok9xA1wrkZi',
				version: 1
			},
			attributes: {
				settings: {
					payment_methods: {
						cards: true,
						ach: true
					}
				},
				name: {
					dba: null,
					legal: null
				},
				address: {
					street_1: null,
					street_2: null,
					city: null,
					state: null,
					zip_code: null,
					country: 'US'
				},
				phone_number: null,
				company_size: null,
				user: {
					is_owner: false
				},
				slug: null,
				terms_agreement: {
					opted_in: false,
					date: null
				}
			},
			links: {
				avatar: null
			},
			metrics: {
				balances: {
					total: 0,
					available: 0,
					scheduled_activities: 0,
					expenses: 0,
					goals: 0,
					vendor_payments: 0,
					security_deposits: 0
				},
				totals: {
					residents: 0,
					units: 0,
					properties: 0
				}
			},
			relationships: {
				user: firebaseAuth.currentUser.uid,
				stripe: null
			}
		}

	}),

	/*
	**  Set up the methods
	*/

	methods: {

		createCompany: function() {

			// Generate UID
			this.new_company.id = this.generateUID();

			// Create the doc
			companiesModel.doc(this.new_company.id).set(this.new_company)
			.then(() => {

				// Update the user record
				this.updateUserCompany();

			})
			.catch(error => {

				// Hide the spinner
				this.hideSpinner();

				// Throw warning message
    			this.$swal('Uh Oh!', 'Looks like we ran into trouble saving your company details.', 'error');

    			// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Create Company - Save New Company in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Company',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		saveOptedIn: function() {

			// Update the doc
			companiesModel.doc(this.company.id).update({

				'attributes.terms_agreement.opted_in': true,
				'attributes.terms_agreement.date': moment().unix(),
				'meta.updated_at': moment().unix()

			})
			.catch(error => {

    			// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Company Terms Agreement in Firestore',
					errorType: 'Firestore Database',
					section: 'Settingss Company',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		storeStripeConnectInfo: function() {

			// Update the doc
			companiesModel.doc(this.company.id).update({

				'relationships.stripe': this.stripe_response,
				'meta.updated_at': moment().unix()

			})
			.then(() => {

				// Hide spinner
				this.hideSpinner();

				// Display message to user
				this.$swal('Success!', 'Welcome back! You are all set up to begin receiving payments from your residents.', 'success');

			})
			.catch(error => {

    			// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Stripe Connect Onboarding Response in Firestore',
					errorType: 'Firestore Database',
					section: 'Settings Company',
					component: 'CompanyRepository',
					error: error
				});

			})

		},

		updateCompanyTotalProperties: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.properties': this.company.metrics.totals.properties + 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Company Total Properties Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Property',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		decreaseCompanyTotalProperties: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.properties': this.company.metrics.totals.properties - 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Decrease Company Total Properties Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Delete Property',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		addOneToCompanyTotalUnits: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.units': this.company.metrics.totals.units + 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Company Total Units Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Unit',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		subtractOneFromCompanyTotalUnits: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.units': this.company.metrics.totals.units - 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Decrease Company Total Units Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Delete Unit',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		addOneToCompanyTotalResidents: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.residents': this.company.metrics.totals.residents + 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update Company Total Residents Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Create Resident',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		subtractOneFromCompanyTotalResidents: function() {

			// Update the total properties metric
			companiesModel.doc(this.company.id).update({

				'metrics.totals.residents': this.company.metrics.totals.residents - 1,
				'meta.updated_at': moment().unix()

			}).catch(error => {

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Decrease Company Total Residents Metric in Firestore',
					errorType: 'Firestore Database',
					section: 'Delete Resident',
					component: 'CompanyRepository',
					error: error
				});

			});

		},

		updatePaymentMethods: function() {

			// Update the settings payment methods
			companiesModel.doc(this.company.id).update({

				'attributes.settings.payment_methods.cards': this.company.attributes.settings.payment_methods.cards,
				'attributes.settings.payment_methods.ach': this.company.attributes.settings.payment_methods.ach,
				'meta.updated_at': moment().unix()

			})
			.then(() => {

				// Hide spinner
				this.hideSpinner();

				// Show success message
				this.$swal('Success!', 'Your payment methods have been updated.', 'success');

			})
			.catch(error => {

				// Hide spinner
				this.hideSpinner();

				// Show success message
				this.$swal('Uh Oh!', 'Looks like we ran into trouble saving your payment methods.', 'success');

				// Send to bugsnag
				this.reportAuthBugsnagError({
					title: 'Update the Payment Methods Preferences in Firestore',
					errorType: 'Firestore Database',
					section: 'Settings',
					component: 'CompanyRepository',
					error: error
				});

			});

		}

	}

}